import { CollapseAllVisibility, IDetailsGroupDividerProps, IDetailsRowProps, IGroup, Toggle } from '@fluentui/react';
import {
  adjustColumnWidth,
  Button,
  buttonStylesGhost,
  CommonStyles,
  debounce,
  DetailsList,
  detailsListStylesGroupCartContents,
  EmptyMessage,
  flattenNestedRows,
  FontWeights,
  getGroupHeight,
  getGroupsWithEmptyGroupsOfNextPage,
  getRowWidthByColumns,
  getStartIndexOfGroup,
  GroupsAndItems,
  IColumnWithDataType,
  IDetailsListProps,
  IGroupWithDataType,
  IListRowProps,
  ListRow,
  listRowStylesContents,
  ListRowType,
  Loader,
  loaderStylesSpinnerXLarge,
  onRenderTagColumn,
  Sizes,
  toggleStylesLabelOnly,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  Feature,
  FeatureSet,
  ListableFeature,
  ListFeaturesResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { FeatureType } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
import { ContentsTitle, defaultPageSize } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { FeatureDetails } from '../FeatureDetails/FeatureDetails';
import { featureListStylesDefault, IFeatureListClassNames, IFeatureListStyles } from './FeatureList.styles';

interface FeatureItem extends Feature {
  anomalyDetection?: boolean;
  featureType?: FeatureType;
  depth?: number;
  hasChild?: boolean;
  lastChild?: boolean;
}

export const getFeatureColumns = (
  isCollapsed = true,
  isChild = false,
  rowWidth = 0,
  onClickOpenFeaturePage?: (item: Feature | ListableFeature) => void,
  isSearchResult = false
): IColumnWithDataType[] => {
  const anomalyDetectionColumn: IColumnWithDataType = {
    key: 'Anomaly Detection',
    name: 'Anomaly Detection',
    fieldName: 'anomalyDetection',
    minWidth: 180,
    onRender: (item) => {
      return (
        <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
          <div
            style={{
              fontWeight: FontWeights.semiBold,
              paddingBottom: 2,
            }}
          >
            Anomaly Detection
          </div>
          <div>
            <Toggle
              // TODO: when it can be editable, it will be editable.
              styles={toggleStylesLabelOnly}
              key="spi"
              label={item.anomalyDetection ? 'True' : 'False'}
              inlineLabel
              checked={item.anomalyDetection}
            />
          </div>
        </div>
      );
    },
  };
  let columns: IColumnWithDataType[] = [
    {
      key: 'Feature Name',
      name: 'Feature Name',
      fieldName: 'description',
      minWidth: 180,
      data: {
        headerFieldName: 'name',
        listCellProps: {
          hasDepth: true,
          emptyMessage: 'No Description',
          onRenderPrefix: (item) => {
            const style: CSSProperties = {
              paddingTop: '14px',
              paddingBottom: '14px',
              marginRight: '8px',
              height: '100%',
            };

            const depth = item.depthWithHasNextSibling ? item.depthWithHasNextSibling.length : 0;

            if (depth === 0) {
              style.position = 'absolute';
              style.left = '18px';
            }
            return item.status ? (
              <>
                <div className="divider" style={style}>
                  <div
                    style={{
                      width: '4px',
                      height: '100%',
                      borderRadius: Sizes.borderRadius,
                      backgroundColor: '#FCAEAE',
                    }}
                  ></div>
                </div>
                {depth === 0 && <div className="divider-margin" style={{ marginRight: '6px' }}></div>}
              </>
            ) : (
              <></>
            );
          },
        },
      },
    },
    anomalyDetectionColumn,
    {
      key: 'Feature Type',
      name: 'Feature Type',
      fieldName: 'featureType',
      minWidth: 150,
      onRender: (item) => onRenderTagColumn(item, 'Feature Type', 'featureType', '#FAE6FF'),
    },
    {
      key: 'Data Type',
      name: 'Data Type',
      fieldName: 'dataType',
      minWidth: 150,
      onRender: (item) => onRenderTagColumn(item, 'Data Type', 'dataType', '#FAE6FF'),
    },
    {
      key: 'Version',
      name: 'Version',
      fieldName: 'version',
      minWidth: 150,
    },
  ];
  const fixedWidthColumnIndexes: number[] = [];
  if (isChild) {
    columns = columns.slice(2);
    columns.push(anomalyDetectionColumn);
    fixedWidthColumnIndexes.push(0, 1, 2, 3);
  } else if (!isCollapsed) {
    fixedWidthColumnIndexes.push(1);
    columns = columns.slice(0, 1);
  }
  if (onClickOpenFeaturePage) {
    columns.push({
      key: 'Details',
      name: 'Details',
      minWidth: isSearchResult ? 215 : 250,
      maxWidth: isSearchResult ? 215 : 250,
      onRender: (item) => (
        <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
          <Button
            styles={buttonStylesGhost}
            text={isSearchResult ? 'Open Feature' : 'Open Feature Page'}
            onClick={() => onClickOpenFeaturePage(item)}
          />
        </div>
      ),
    });
  }
  return rowWidth ? adjustColumnWidth(rowWidth, columns, fixedWidthColumnIndexes) : columns;
};

export const getStatisticsChartData = (d: Feature) => {
  const s = d.profile?.statistics || {};
  return [
    {
      name: 'Max',
      value: s.max,
    },
    {
      name: 'Min',
      value: s.min,
    },
    {
      name: 'Median',
      value: s.median,
    },
    {
      name: 'Stddev',
      value: s.stddev,
    },
    {
      name: 'RecCount',
      value: s.stddevRecCount,
    },
    {
      name: 'NullCount',
      value: s.nullCount,
    },
    {
      name: 'NanCount',
      value: s.nanCount,
    },
    {
      name: 'Unique',
      value: s.unique,
    },
  ];
};

export const convertToFeatureListData = (d: Feature) => ({
  ...d,
  anomalyDetection: d.monitoring?.anomalyDetection,
  featureType: d.profile?.featureType,
  childCount: 1,
  statisticsChartData: getStatisticsChartData(d),
});

const getAPI = (
  searchText: string | undefined,
  pageSize: number,
  pageToken: string | undefined,
  featureSetId: string | undefined,
  reviewId: string | undefined,
  title?: ContentsTitle,
  featureSetVersion?: string
) => {
  switch (title) {
    case ContentsTitle.pendingReviews:
      return fsApi(CoreService.ListFeaturesToReview, {
        reviewId,
      });
    case ContentsTitle.pendingFeatureSets:
      return fsApi(CoreService.ListFeaturesInReview, {
        reviewId,
      });
    default:
      return fsApi(CoreService.ListFeatures, {
        query: searchText,
        featureSetId,
        featureSetVersion,
        pageSize,
        pageToken,
      });
  }
};

interface IFeatureListProps {
  title?: ContentsTitle;
  reviewId?: string;
  data: FeatureSet;
  searchText?: string;
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
  onOpenFeaturePage: (data: Feature) => void;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
}

export const FeatureList = ({
  title,
  reviewId,
  data,
  searchText,
  onOpenFeaturePage,
  onUpdateData,
  onScroll,
}: IFeatureListProps) => {
  const classNames = useClassNames<IFeatureListStyles, IFeatureListClassNames>('FeatureList', featureListStylesDefault);
  const [groupsAndItems, setGroupsAndItems] = useState<GroupsAndItems>({
    groups: [],
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const refIsLoadingPage = useRef(false);
  const refPageSize = useRef(defaultPageSize);
  const { addToast } = useToast();
  const load = useCallback(
    async ({ groups, items, pageToken }: GroupsAndItems = { groups: [], items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading features...`);
      try {
        const isFirstPage = groups.length === 0;
        const pageSize = refPageSize.current;
        const { features = [], nextPageToken = '' }: ListFeaturesResponse = (await getAPI(
          searchText,
          pageSize,
          pageToken,
          data?.id,
          reviewId,
          title,
          data?.version
        )) as ListFeaturesResponse;
        const startIndex = getStartIndexOfGroup(groups, pageSize);
        const flatFeatures = flattenNestedRows(features, 'nestedFeatures');
        const groupsOfPages = getGroupsWithEmptyGroupsOfNextPage(
          groups,
          features,
          pageSize,
          convertToFeatureListData,
          undefined,
          undefined,
          () => flatFeatures
        );
        flatFeatures.forEach((item, i) => (items[i + startIndex] = convertToFeatureListData(item)));
        const newData = { groups: [...groupsOfPages], items: [...items], pageToken: nextPageToken };
        if (isFirstPage && newData.groups.length === 1) {
          newData.groups[0].isCollapsed = false;
        }
        setGroupsAndItems(newData);
        onUpdateData && onUpdateData(newData.groups.length, pageSize, newData.pageToken, ContentsTitle.features);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading features: `));
      } finally {
        if (hasLoading) setLoadingMessage('');
        refIsLoadingPage.current = false;
      }
    },
    [searchText, data.id, onUpdateData, addToast]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesGroupCartContents,
    columns: getFeatureColumns(),
    isHeaderVisible: false,
    groups: groupsAndItems.groups,
    items: groupsAndItems.items,
    getGroupHeight: (group: IGroup) => getGroupHeight(group, ListRowType.GroupHeaderCard, ListRowType.Contents),
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    groupProps: {
      collapseAllVisibility: CollapseAllVisibility.hidden,
      showEmptyGroups: true,
      onRenderHeader: (props?: IDetailsGroupDividerProps) => {
        const { columns = [], group, onToggleCollapse } = props || {};
        const { data, isCollapsed, count } = group as IGroupWithDataType;
        if (!data.hasGroupHeaderData && !refIsLoadingPage.current) {
          refIsLoadingPage.current = true;
          onLoad(groupsAndItems, false);
        }
        return (
          <ListRow
            data={data.data}
            columns={
              isCollapsed
                ? columns
                : getFeatureColumns(isCollapsed, false, getRowWidthByColumns(columns), () =>
                    onOpenFeaturePage(data.data)
                  )
            }
            group={group}
            collapsed={isCollapsed}
            type={ListRowType.GroupHeaderCard}
            hasOpenClose={count > 0}
            loading={!data.hasGroupHeaderData}
            onOpen={() => onToggleCollapse!(group!)}
          />
        );
      },
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [], rowWidth } = props || {};
      const listRowProps: IListRowProps<FeatureItem> = {
        data: item,
        columns: getFeatureColumns(false, true, getRowWidthByColumns(columns)),
        type: ListRowType.Group,
        styles: listRowStylesContents,
        contents: <FeatureDetails data={item} watchWidth={rowWidth} onOpenFeaturePage={onOpenFeaturePage} />,
      };
      return <ListRow {...listRowProps} />;
    },
  };

  useEffect(() => {
    if (data.id) {
      load();
    }
  }, [load, data.id]);

  return (
    <div className={classNames.root} data-test="feature-list">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <div className={classNames.featureList} data-is-scrollable="true" onScroll={onScroll}>
          {groupsAndItems.items.length === 0 ? (
            <EmptyMessage message="No Features Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      )}
    </div>
  );
};
